<template>
    <app-modal v-model="isDialogOpened"
               size="md"
               :ok-loading="contactResponsiblePending"
               :ok-disabled="okDisabled"
               :cancel-disabled="contactResponsiblePending"
               @ok="prepareContactResponsible"
               @cancel="closeDialog"
    >
      <template #title>{{$t(`${TRANSLATION_KEY_PREFIX}.title`)}}</template>
      <template #body>
        <div class="d-flex flex-column row-gap-4">
          <template v-if="isBulkEdition">
            <app-bulk-list
              :items="tasks"
              :items-name="$t(`${TRANSLATION_KEY_PREFIX}.bulkListItemsName`)"
            >
              <template #default="{ item }">
                <div class="d-flex align-start column-gap-2">
                  <span>{{ item.positionDisplay }}</span> <span>{{ item.title }}</span>
                </div>
              </template>
            </app-bulk-list>
          </template>
          <div class="additional-info-editor"
               :class="{'is-editing': isEditing}"
          >
            <VisualTextEditor v-model:value="message"
                              counter="footer"
                              :error-messages="messageErrors"
                              :max-length="messageMaxLength"
                              @blur="isEditing = false"
                              @focus="isEditing = true"
            />
          </div>
          <v-checkbox v-model="getMessageCopy"
                      :label="$t(`${TRANSLATION_KEY_PREFIX}.getMessageCopy`)"
                      class="mt-0"
                      hide-details
          />
          <template v-if="isBulkEdition && someSelectedTasksHaveNoResponsibles">
            <app-alert-in-page
              :dismissible="false"
              :has-left-border="false"
              variant="info"
            >
              <template #title>{{ $t(`${TRANSLATION_KEY_PREFIX}.alertMissingResponsibleTitle`) }}</template>
              <div>{{ $t(`${TRANSLATION_KEY_PREFIX}.alertMissingResponsibleText`) }}</div>
            </app-alert-in-page>
          </template>
        </div>
      </template>
    </app-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { dialogMixin } from '@/mixins/dialogMixin'
import { CONTACT_RESPONSIBLE } from '@/store/modules/checklist/action_types'

const MESSAGE_MAX_LENGTH = 400
const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.ContactResponsibleDialog'

export default {
  name: 'ContactResponsibleDialog',
  components: { VisualTextEditor },
  mixins: [dialogMixin],
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    tasks: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      message: '',
      messageMaxLength: MESSAGE_MAX_LENGTH,
      getMessageCopy: false,
      isEditing: false,
    }
  },
  computed: {
    ...mapState('checklist', ['contactResponsiblePending']),
    ...mapGetters('checklist', ['someSelectedTasksHaveNoResponsibles']),
    isBulkEdition () {
      return this.tasks.length > 1
    },
    messageLength () {
      return escapeHtml(this.message).length
    },
    messageIsTooLong () {
      return this.messageLength > this.messageMaxLength
    },
    messageErrors () {
      const errors = []

      if (this.messageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    okDisabled () {
      return this.messageIsTooLong || this.contactResponsiblePending
    },
  },
  methods: {
    ...mapActions('checklist', [CONTACT_RESPONSIBLE]),
    async prepareContactResponsible () {
      const task_ids = this.tasks.map(task => task.id)

      try {
        await this.CONTACT_RESPONSIBLE({
          mnemo: this.mnemo,
          data: {
            chaseMsg: this.message,
            ccAuthor: this.getMessageCopy,
            task_ids: task_ids,
          },
        })
        toast.success(this.$t('project.checklist.dialogs.ContactResponsibleDialog.contactResponsibleSuccess'))

        this.closeDialog()
      } catch (e) {
        console.error('Error contacting responsible', e)
        toast.error(this.$t('project.checklist.dialogs.ContactResponsibleDialog.contactResponsibleError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.additional-info-editor {
  outline: 1px solid rgb(var(--v-theme-border-primary));
  background-color: rgb(var(--v-theme-surface-primary));
  @include border-radius('border-radius-3');
  overflow: hidden;
  position: relative;

  &.is-editing {
    outline: 2px solid rgb(var(--v-theme-border-info-bold));
  }

  :deep(.editor__content .ProseMirror) {
    border: none !important;
    max-height: 60px;
    min-height: auto;
  }
  :deep(.editor__menubar) {
    border: none !important;
    padding: 4px 0;
  }
  :deep(.editor__content .ProseMirror p.is-editor-empty:first-child::before) {
    color: rgb(var(--v-theme-text-placeholder));
  }
  :deep(.editor__footer) {
    flex-direction: column;
  }
  :deep(.editor__errors) {
    display: none;
  }
}
:deep(.text-counter) {
  bottom: 12px;
  left: 16px;
  position: absolute;
}
:deep(.additional-info-editor .editor__content) {
  padding-bottom: 38px;
}
</style>
